/** @module The rectangle the card belongs in: the person aims, the app watches only there. */

/** A card is 63 x 88 mm. */
export const CARD_ASPECT = 63 / 88;
/** Share of the shorter viewport side the frame uses; leaves room for a hand. */
const FILL = 0.82;

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The guide rectangle in fractions of the video frame, centred and card shaped. */
export function guideRect(frameWidth: number, frameHeight: number): Rect {
  if (frameWidth <= 0 || frameHeight <= 0) return { x: 0, y: 0, width: 1, height: 1 };

  // Fit a portrait card into the frame, whichever way the phone is held.
  let height = FILL;
  let width = (height * frameHeight * CARD_ASPECT) / frameWidth;
  if (width > FILL) {
    width = FILL;
    height = (width * frameWidth) / CARD_ASPECT / frameHeight;
  }
  return { x: (1 - width) / 2, y: (1 - height) / 2, width, height };
}

/**
 * The rectangle reshaped to a card, keeping its centre and its height.
 *
 * The frame is stored in fractions of the video. When the camera changes its format
 * (640 x 480 became 3840 x 2160 once the scan page asked for 4K), the same fractions make
 * a frame that is no longer card shaped: it came out a third too narrow, people fitted the
 * card to its height, and the photo cut off both sides of the card.
 */
export function cardShaped(rect: Rect, frameWidth: number, frameHeight: number): Rect {
  if (frameWidth <= 0 || frameHeight <= 0) return rect;
  let height = Math.min(rect.height, 1);
  let width = (height * frameHeight * CARD_ASPECT) / frameWidth;
  if (width > 1) {
    width = 1;
    height = (width * frameWidth) / CARD_ASPECT / frameHeight;
  }
  const centreX = rect.x + rect.width / 2;
  const centreY = rect.y + rect.height / 2;
  return {
    x: Math.min(Math.max(centreX - width / 2, 0), 1 - width),
    y: Math.min(Math.max(centreY - height / 2, 0), 1 - height),
    width,
    height,
  };
}

/** The same rectangle in pixels of a photo of the given size. */
export function toPixels(rect: Rect, width: number, height: number): Rect {
  return {
    x: Math.round(rect.x * width),
    y: Math.round(rect.y * height),
    width: Math.round(rect.width * width),
    height: Math.round(rect.height * height),
  };
}

/** The four corners of the rectangle, clockwise from top left, for the quad hint. */
export function toQuad(rect: Rect, width: number, height: number): number[][] {
  const box = toPixels(rect, width, height);
  return [
    [box.x, box.y],
    [box.x + box.width, box.y],
    [box.x + box.width, box.y + box.height],
    [box.x, box.y + box.height],
  ];
}
